import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { 
  Users, DollarSign, Settings, Search, Edit2, Check, X, 
  ArrowDownCircle, ArrowUpCircle, Loader2, AlertTriangle,
  Ticket, Trash2, Ban, Save, MessageSquare, Play, Square, RefreshCw,
  Bell, Send, Info, CheckCircle2, XCircle, Lock, Unlock, Shield, Eye
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);
  
  // Notification form
  const [newNotification, setNewNotification] = useState({
    title: "",
    message: "",
    type: "info",
    target: "all"
  });
  const [sendingNotification, setSendingNotification] = useState(false);
  const [voucherEditValues, setVoucherEditValues] = useState({});
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Modal para mensaje de retiro
  const [messageModalUser, setMessageModalUser] = useState(null);
  const [withdrawalMessageText, setWithdrawalMessageText] = useState("");
  const [savingMessage, setSavingMessage] = useState(false);

  // Modal para bloquear copy trading
  const [blockModalUser, setBlockModalUser] = useState(null);
  const [blockMessageText, setBlockMessageText] = useState("");
  const [savingBlock, setSavingBlock] = useState(false);

  // Modal para rechazar retiro
  const [rejectModal, setRejectModal] = useState(null); // {transactionId, userId, userEmail, userName}
  const [rejectTitle, setRejectTitle] = useState("");
  const [rejectMessage, setRejectMessage] = useState("");
  const [sendingReject, setSendingReject] = useState(false);

  // Plantillas predefinidas para rechazo (se llenarán después)
  const rejectTemplates = [
    { id: 1, title: "Plantilla 1", message: "" },
    { id: 2, title: "Plantilla 2", message: "" },
    { id: 3, title: "Plantilla 3", message: "" },
    { id: 4, title: "Plantilla 4", message: "" },
    { id: 5, title: "Plantilla 5", message: "" },
  ];

  // Plantillas predefinidas para mensaje de retiro (se llenarán después)
  const withdrawalMessageTemplates = [
    { id: 1, message: "" },
    { id: 2, message: "" },
    { id: 3, message: "" },
    { id: 4, message: "" },
    { id: 5, message: "" },
  ];

  // Aplicar plantilla de mensaje de retiro
  const applyWithdrawalTemplate = (template) => {
    setWithdrawalMessageText(template.message);
  };

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Sesión expirada. Inicia sesión de nuevo.");
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      
      // Cargar cada endpoint por separado para que un fallo no bloquee los demás
      const [usersRes, transRes, vouchersRes, settingsRes, notificationsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers }).catch(e => {
          console.error("Error cargando usuarios:", e);
          return { data: { users: [] } };
        }),
        axios.get(`${API}/admin/transactions`, { headers }).catch(e => {
          console.error("Error cargando transacciones:", e);
          return { data: { transactions: [] } };
        }),
        axios.get(`${API}/admin/vouchers`, { headers }).catch(e => {
          console.error("Error cargando vouchers:", e);
          return { data: { vouchers: [] } };
        }),
        axios.get(`${API}/admin/settings`, { headers }).catch(e => {
          console.error("Error cargando settings:", e);
          return { data: {} };
        }),
        axios.get(`${API}/admin/notifications`, { headers }).catch(e => {
          console.error("Error cargando notificaciones:", e);
          return { data: { notifications: [] } };
        }),
        axios.get(`${API}/admin/security-logs`, { headers }).catch(e => {
          console.error("Error cargando logs:", e);
          return { data: { logs: [] } };
        })
      ]);
      
      setUsers(usersRes.data.users || []);
      setTransactions(transRes.data.transactions || []);
      setVouchers(vouchersRes.data.vouchers || []);
      setSettings(settingsRes.data || {});
      setNotifications(notificationsRes.data.notifications || []);
      setSecurityLogs(logsRes.data.logs || []);
      setLastUpdate(new Date());
    } catch (e) {
      console.error("Error general:", e);
      // Solo mostrar error si es un problema de autenticación
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error("Sesión expirada. Inicia sesión de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Send notification
  const handleSendNotification = async () => {
    if (!newNotification.title.trim() || !newNotification.message.trim()) {
      toast.error("Completa el título y mensaje");
      return;
    }
    
    setSendingNotification(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/notifications`, newNotification, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notificación enviada");
      setNewNotification({ title: "", message: "", type: "info", target: "all" });
      fetchData();
    } catch (e) {
      toast.error("Error al enviar notificación");
    } finally {
      setSendingNotification(false);
    }
  };

  // Delete notification
  const handleDeleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notificación eliminada");
      fetchData();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 5 seconds for real-time updates
    const interval = setInterval(() => {
      fetchData();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleEditUser = (user) => {
    setEditingUser(user.id);
    setEditValues({
      balance: user.balance,
      total_profit: user.current_profit || user.total_profit || 0,
      profit_rate: user.profit_rate * 100,
      copy_duration_hours: user.copy_duration_hours || "",
      blocked: user.blocked || false
    });
  };

  const handleSaveUser = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${userId}`, {
        balance: parseFloat(editValues.balance),
        total_profit: parseFloat(editValues.total_profit),
        profit_rate: parseFloat(editValues.profit_rate) / 100,
        copy_duration_hours: editValues.copy_duration_hours ? parseFloat(editValues.copy_duration_hours) : null,
        blocked: editValues.blocked
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Usuario actualizado correctamente");
      setEditingUser(null);
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar");
    }
  };

  // Parar o iniciar copy trading
  const handleToggleCopyTrading = async (user) => {
    try {
      const token = localStorage.getItem("token");
      
      if (user.is_copying) {
        // Parar copy trading - guardar las ganancias actuales
        await axios.put(`${API}/admin/users/${user.id}`, {
          is_copying: false,
          total_profit: user.current_profit || user.total_profit || 0
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Copy trading PARADO para ${user.full_name}`);
      } else {
        // Iniciar copy trading
        await axios.put(`${API}/admin/users/${user.id}`, {
          is_copying: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Copy trading INICIADO para ${user.full_name}`);
      }
      fetchData();
    } catch (e) {
      toast.error("Error al cambiar estado de copy trading");
    }
  };

  // Abrir modal de mensaje de retiro
  const openMessageModal = (user) => {
    setMessageModalUser(user);
    setWithdrawalMessageText(user.withdrawal_message || "");
  };

  // Guardar mensaje de retiro personalizado
  const handleSaveWithdrawalMessage = async () => {
    if (!messageModalUser) return;
    
    setSavingMessage(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${messageModalUser.id}`, {
        withdrawal_message: withdrawalMessageText.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (withdrawalMessageText.trim()) {
        toast.success(`Mensaje de retiro configurado para ${messageModalUser.full_name}`);
      } else {
        toast.success(`Mensaje de retiro eliminado para ${messageModalUser.full_name}`);
      }
      
      setMessageModalUser(null);
      setWithdrawalMessageText("");
      fetchData();
    } catch (e) {
      toast.error("Error al guardar el mensaje");
    } finally {
      setSavingMessage(false);
    }
  };

  // Abrir modal de bloqueo de copy trading
  const openBlockModal = (user) => {
    setBlockModalUser(user);
    setBlockMessageText(user.copy_block_message || "Tu inversión actual ha finalizado. Para seguir copiando operaciones y generando ganancias, realiza un nuevo depósito.");
  };

  // Guardar bloqueo de copy trading
  const handleSaveBlock = async (block) => {
    if (!blockModalUser) return;
    
    setSavingBlock(true);
    try {
      const token = localStorage.getItem("token");
      
      const updateData = {
        copy_trading_blocked: block,
        copy_block_message: block ? blockMessageText.trim() : null
      };
      
      // Si bloqueamos, también paramos el copy trading activo
      if (block && blockModalUser.is_copying) {
        updateData.is_copying = false;
        updateData.total_profit = blockModalUser.current_profit || blockModalUser.total_profit || 0;
      }
      
      await axios.put(`${API}/admin/users/${blockModalUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (block) {
        toast.success(`Copy trading BLOQUEADO para ${blockModalUser.full_name}. El usuario no puede reiniciarlo.`);
      } else {
        toast.success(`Copy trading DESBLOQUEADO para ${blockModalUser.full_name}. El usuario puede iniciarlo.`);
      }
      
      setBlockModalUser(null);
      setBlockMessageText("");
      fetchData();
    } catch (e) {
      toast.error("Error al guardar el bloqueo");
    } finally {
      setSavingBlock(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Usuario eliminado");
      fetchData();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const handleBlockUser = async (userId, blocked) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${userId}`, { blocked }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(blocked ? "Usuario bloqueado" : "Usuario desbloqueado");
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar");
    }
  };

  const handleTransactionStatus = async (transactionId, status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/transactions/${transactionId}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Transacción ${status === 'approved' ? 'aprobada' : 'rechazada'}`);
      fetchData();
    } catch (e) {
      toast.error("Error al procesar");
    }
  };

  // Abrir modal de rechazo
  const openRejectModal = (transaction) => {
    // Buscar info del usuario
    const user = users.find(u => u.id === transaction.user_id);
    setRejectModal({
      transactionId: transaction.id,
      userId: transaction.user_id,
      userEmail: user?.email || transaction.user_email,
      userName: user?.full_name || "Usuario"
    });
    setRejectTitle("");
    setRejectMessage("");
  };

  // Enviar rechazo con notificación
  const handleRejectWithReason = async () => {
    if (!rejectModal) return;
    
    setSendingReject(true);
    try {
      const token = localStorage.getItem("token");
      
      // 1. Rechazar la transacción
      await axios.put(`${API}/admin/transactions/${rejectModal.transactionId}?status=rejected`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // 2. Si hay motivo, enviar notificación urgente + email
      if (rejectTitle.trim() || rejectMessage.trim()) {
        await axios.post(`${API}/admin/reject-notification`, {
          user_id: rejectModal.userId,
          title: rejectTitle.trim() || "Retiro Rechazado",
          message: rejectMessage.trim() || "Tu solicitud de retiro ha sido rechazada."
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success("Retiro rechazado y usuario notificado");
      setRejectModal(null);
      fetchData();
    } catch (e) {
      toast.error("Error al procesar el rechazo");
    } finally {
      setSendingReject(false);
    }
  };

  // Aplicar plantilla de rechazo
  const applyRejectTemplate = (template) => {
    setRejectTitle(template.title);
    setRejectMessage(template.message);
  };

  const handleEditVoucher = (voucher) => {
    setEditingVoucher(voucher.id);
    setVoucherEditValues({
      amount: voucher.amount,
      status: voucher.status
    });
  };

  const handleSaveVoucher = async (voucherId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/vouchers/${voucherId}?amount=${voucherEditValues.amount}&status=${voucherEditValues.status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Voucher actualizado");
      setEditingVoucher(null);
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar voucher");
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSavingSettings(false);
    }
  };

  const filteredUsers = users.filter(u => 
    !u.is_admin && (
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const pendingTransactions = transactions.filter(t => t.status === 'pending');
  const pendingVouchers = vouchers.filter(v => v.status === 'pending');
  
  // Calcular totales
  const totalBalance = users.filter(u => !u.is_admin).reduce((sum, u) => sum + (u.balance || 0), 0);
  const totalProfit = users.filter(u => !u.is_admin).reduce((sum, u) => sum + (u.current_profit || u.total_profit || 0), 0);
  const totalMoney = totalBalance + totalProfit;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#F6465D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* MODAL PARA MENSAJE DE RETIRO PERSONALIZADO */}
      {messageModalUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="card-dark border-2 border-[#F0B90B] w-full max-w-lg">
            <CardHeader className="border-b border-[#2B3139] bg-[#F0B90B]/10">
              <CardTitle className="text-[#EAECEF] flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#F0B90B]" />
                Mensaje de Retiro Personalizado
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="bg-[#0B0E11] rounded-lg p-4 border border-[#2B3139]">
                <p className="text-sm text-[#848E9C]">Usuario:</p>
                <p className="text-[#EAECEF] font-medium">{messageModalUser.full_name}</p>
                <p className="text-xs text-[#848E9C]">{messageModalUser.email}</p>
              </div>

              {/* Botones de plantillas */}
              <div>
                <Label className="text-[#848E9C] text-xs mb-2 block">Plantillas rápidas:</Label>
                <div className="flex gap-2">
                  {withdrawalMessageTemplates.map((template) => (
                    <Button
                      key={template.id}
                      size="sm"
                      variant="outline"
                      className="border-[#2B3139] text-[#848E9C] hover:bg-[#F0B90B]/20 hover:text-[#F0B90B] hover:border-[#F0B90B] w-8 h-8 p-0"
                      onClick={() => applyWithdrawalTemplate(template)}
                      data-testid={`withdrawal-template-${template.id}`}
                    >
                      {template.id}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#EAECEF] font-semibold">
                  Mensaje que verá el usuario al intentar retirar:
                </Label>
                <Textarea
                  value={withdrawalMessageText}
                  onChange={(e) => setWithdrawalMessageText(e.target.value)}
                  placeholder="Ej: Tu operación de copy trading sigue activa. Debes esperar 72 horas más para poder retirar tus ganancias."
                  className="input-dark min-h-[120px] text-base"
                  data-testid="withdrawal-message-input"
                />
                <p className="text-xs text-[#848E9C]">
                  Si dejas este campo vacío, el usuario podrá realizar retiros normalmente.
                </p>
              </div>

              <div className="bg-[#F0B90B]/10 rounded-lg p-4 border border-[#F0B90B]/30">
                <p className="text-sm text-[#F0B90B]">
                  <strong>⚠️ Importante:</strong> Cuando configures un mensaje, el usuario verá esta pantalla en lugar del formulario de retiro cuando intente retirar su dinero.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  className="flex-1 bg-[#0ECB81] hover:bg-[#0ECB81]/80 text-white font-bold py-3"
                  onClick={handleSaveWithdrawalMessage}
                  disabled={savingMessage}
                  data-testid="save-withdrawal-message"
                >
                  {savingMessage ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Save className="w-5 h-5 mr-2" />
                  )}
                  GUARDAR MENSAJE
                </Button>
                <Button 
                  variant="outline"
                  className="border-[#2B3139] text-[#848E9C] hover:bg-[#2B3139]"
                  onClick={() => {
                    setMessageModalUser(null);
                    setWithdrawalMessageText("");
                  }}
                >
                  Cancelar
                </Button>
              </div>
              
              {messageModalUser.withdrawal_message && (
                <Button 
                  variant="outline"
                  className="w-full border-[#F6465D] text-[#F6465D] hover:bg-[#F6465D]/10"
                  onClick={() => {
                    setWithdrawalMessageText("");
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar mensaje actual
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* MODAL PARA BLOQUEAR COPY TRADING */}
      {blockModalUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="card-dark border-2 border-[#F6465D] w-full max-w-lg">
            <CardHeader className="border-b border-[#2B3139] bg-[#F6465D]/10">
              <CardTitle className="text-[#EAECEF] flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#F6465D]" />
                Bloquear Copy Trading
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="bg-[#0B0E11] rounded-lg p-4 border border-[#2B3139]">
                <p className="text-sm text-[#848E9C]">Usuario:</p>
                <p className="text-[#EAECEF] font-medium">{blockModalUser.full_name}</p>
                <p className="text-xs text-[#848E9C]">{blockModalUser.email}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-[#848E9C]">Estado actual:</span>
                  {blockModalUser.copy_trading_blocked ? (
                    <Badge className="bg-[#F6465D]/20 text-[#F6465D]">
                      <Lock className="w-3 h-3 mr-1" />
                      BLOQUEADO
                    </Badge>
                  ) : (
                    <Badge className="bg-[#0ECB81]/20 text-[#0ECB81]">
                      <Unlock className="w-3 h-3 mr-1" />
                      DESBLOQUEADO
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#EAECEF] font-semibold">
                  Mensaje que verá el usuario cuando intente iniciar copy trading:
                </Label>
                <Textarea
                  value={blockMessageText}
                  onChange={(e) => setBlockMessageText(e.target.value)}
                  placeholder="Ej: Tu operación ha finalizado. No puedes iniciar una nueva operación en este momento."
                  className="input-dark min-h-[100px] text-base"
                  data-testid="block-message-input"
                />
              </div>

              <div className="bg-[#F6465D]/10 rounded-lg p-4 border border-[#F6465D]/30">
                <p className="text-sm text-[#F6465D]">
                  <strong>🔒 Diferencia con "Parar":</strong>
                </p>
                <ul className="text-sm text-[#848E9C] mt-2 space-y-1 list-disc list-inside">
                  <li><strong>Parar:</strong> Detiene la operación, pero el usuario puede reiniciarla</li>
                  <li><strong>Bloquear:</strong> Detiene la operación y el usuario NO puede reiniciarla. Verá el mensaje que configures.</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-2">
                {!blockModalUser.copy_trading_blocked ? (
                  <Button 
                    className="flex-1 bg-[#F6465D] hover:bg-[#F6465D]/80 text-white font-bold py-3"
                    onClick={() => handleSaveBlock(true)}
                    disabled={savingBlock}
                    data-testid="confirm-block"
                  >
                    {savingBlock ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Lock className="w-5 h-5 mr-2" />
                    )}
                    BLOQUEAR COPY TRADING
                  </Button>
                ) : (
                  <Button 
                    className="flex-1 bg-[#0ECB81] hover:bg-[#0ECB81]/80 text-white font-bold py-3"
                    onClick={() => handleSaveBlock(false)}
                    disabled={savingBlock}
                    data-testid="confirm-unblock"
                  >
                    {savingBlock ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Unlock className="w-5 h-5 mr-2" />
                    )}
                    DESBLOQUEAR COPY TRADING
                  </Button>
                )}
                <Button 
                  variant="outline"
                  className="border-[#2B3139] text-[#848E9C] hover:bg-[#2B3139]"
                  onClick={() => {
                    setBlockModalUser(null);
                    setBlockMessageText("");
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Admin Header */}
      <div className="flex items-center justify-between">
        <div className="border-l-4 border-[#F6465D] pl-4">
          <h1 className="text-2xl font-bold text-[#EAECEF] flex items-center gap-2">
            <Settings className="w-6 h-6 text-[#F6465D]" />
            Panel de Administración
          </h1>
          <p className="text-[#848E9C]">Gestiona usuarios, transacciones y configuración</p>
        </div>
        <Button 
          variant="outline" 
          className="border-[#2B3139] text-[#848E9C] hover:text-[#0ECB81]"
          onClick={fetchData}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="card-dark border-t-2 border-t-[#F6465D]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#848E9C]">Usuarios</p>
                <p className="text-2xl font-bold font-mono text-[#EAECEF]">{users.filter(u => !u.is_admin).length}</p>
              </div>
              <Users className="w-6 h-6 text-[#F6465D]" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-dark border-t-2 border-t-[#FCD535]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#848E9C]">Balances</p>
                <p className="text-xl font-bold font-mono text-[#EAECEF]">
                  {totalBalance.toLocaleString('es-ES', {minimumFractionDigits: 2})}€
                </p>
              </div>
              <DollarSign className="w-6 h-6 text-[#FCD535]" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-dark border-t-2 border-t-[#0ECB81]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#848E9C]">Ganancias</p>
                <p className="text-xl font-bold font-mono text-[#0ECB81]">
                  {totalProfit.toLocaleString('es-ES', {minimumFractionDigits: 2})}€
                </p>
              </div>
              <ArrowUpCircle className="w-6 h-6 text-[#0ECB81]" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-dark border-t-2 border-t-[#3B82F6]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#848E9C]">TOTAL</p>
                <p className="text-xl font-bold font-mono text-[#3B82F6]">
                  {totalMoney.toLocaleString('es-ES', {minimumFractionDigits: 2})}€
                </p>
              </div>
              <DollarSign className="w-6 h-6 text-[#3B82F6]" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-dark border-t-2 border-t-[#9B59B6]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#848E9C]">Pendientes</p>
                <p className="text-2xl font-bold font-mono text-[#9B59B6]">{pendingTransactions.length + pendingVouchers.length}</p>
              </div>
              <AlertTriangle className="w-6 h-6 text-[#9B59B6]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Real-time indicator */}
      <div className="flex items-center justify-between text-xs text-[#848E9C] bg-[#181A20] rounded-lg px-4 py-2 border border-[#2B3139]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#0ECB81] rounded-full animate-pulse" />
          <span>Actualización automática cada 5 segundos</span>
        </div>
        <span>Última actualización: {lastUpdate.toLocaleTimeString()}</span>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-[#181A20] border border-[#2B3139] flex-wrap h-auto p-1">
          <TabsTrigger value="users" className="data-[state=active]:bg-[#F6465D] data-[state=active]:text-white" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" />
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="transactions" className="data-[state=active]:bg-[#F6465D] data-[state=active]:text-white" data-testid="tab-transactions">
            <DollarSign className="w-4 h-4 mr-2" />
            Transacciones
            {pendingTransactions.length > 0 && (
              <Badge className="ml-2 bg-[#F0B90B] text-[#0B0E11]">{pendingTransactions.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="vouchers" className="data-[state=active]:bg-[#F6465D] data-[state=active]:text-white" data-testid="tab-vouchers">
            <Ticket className="w-4 h-4 mr-2" />
            Vouchers
            {pendingVouchers.length > 0 && (
              <Badge className="ml-2 bg-[#9B59B6] text-white">{pendingVouchers.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-[#F6465D] data-[state=active]:text-white" data-testid="tab-notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notificaciones
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-[#F6465D] data-[state=active]:text-white" data-testid="tab-security">
            <Shield className="w-4 h-4 mr-2" />
            Seguridad
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-[#F6465D] data-[state=active]:text-white" data-testid="tab-settings">
            <Settings className="w-4 h-4 mr-2" />
            Config
          </TabsTrigger>
        </TabsList>

        {/* USERS TAB */}
        <TabsContent value="users" className="mt-4">
          <Card className="card-dark">
            <CardHeader className="border-b border-[#2B3139]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-[#EAECEF]">Gestión de Usuarios</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#848E9C]" />
                  <Input 
                    placeholder="Buscar usuario..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-dark pl-10"
                    data-testid="search-users"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="table-dark">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th className="text-right">Balance</th>
                      <th className="text-right">Ganancias</th>
                      <th className="text-right">TOTAL</th>
                      <th className="text-center">Copy Trading</th>
                      <th className="text-center">Bloquear Op.</th>
                      <th className="text-center">Msg Retiro</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} data-testid={`user-row-${user.id}`} className={user.blocked ? 'opacity-50' : ''}>
                        <td>
                          <div>
                            <p className="font-medium text-[#EAECEF]">{user.full_name}</p>
                            <p className="text-xs text-[#848E9C]">{user.email}</p>
                            <p className="text-xs text-[#FCD535]">Tasa: {(user.profit_rate * 100).toFixed(1)}%/h</p>
                          </div>
                        </td>
                        <td className="text-right">
                          {editingUser === user.id ? (
                            <Input 
                              type="number"
                              value={editValues.balance}
                              onChange={(e) => setEditValues({...editValues, balance: e.target.value})}
                              className="input-dark w-28 text-right font-mono"
                              step="0.01"
                              data-testid="edit-balance"
                            />
                          ) : (
                            <span className="font-mono text-[#EAECEF]">{(user.balance || 0).toLocaleString('es-ES', {minimumFractionDigits: 2})}€</span>
                          )}
                        </td>
                        <td className="text-right">
                          {editingUser === user.id ? (
                            <Input 
                              type="number"
                              value={editValues.total_profit}
                              onChange={(e) => setEditValues({...editValues, total_profit: e.target.value})}
                              className="input-dark w-28 text-right font-mono"
                              step="0.01"
                              data-testid="edit-profit"
                            />
                          ) : (
                            <div>
                              <span className="font-mono text-[#0ECB81]">+{(user.current_profit || user.total_profit || 0).toLocaleString('es-ES', {minimumFractionDigits: 2})}€</span>
                              {user.is_copying && user.hours_active > 0 && (
                                <p className="text-xs text-[#848E9C]">{user.hours_active.toFixed(1)}h activo</p>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="text-right">
                          <span className="font-mono text-[#3B82F6] font-bold text-lg">
                            {((user.balance || 0) + (user.current_profit || user.total_profit || 0)).toLocaleString('es-ES', {minimumFractionDigits: 2})}€
                          </span>
                        </td>
                        {/* COLUMNA COPY TRADING */}
                        <td className="text-center">
                          <Button 
                            size="sm" 
                            className={`min-w-[100px] ${
                              user.is_copying 
                                ? 'bg-[#0ECB81] hover:bg-[#F6465D] text-white' 
                                : 'bg-[#2B3139] hover:bg-[#0ECB81] text-[#848E9C] hover:text-white'
                            }`}
                            onClick={() => handleToggleCopyTrading(user)}
                            disabled={user.copy_trading_blocked}
                            data-testid={`toggle-copy-${user.id}`}
                            title={user.copy_trading_blocked ? "Debes desbloquear primero" : ""}
                          >
                            {user.is_copying ? (
                              <>
                                <Square className="w-4 h-4 mr-1" />
                                PARAR
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-1" />
                                INICIAR
                              </>
                            )}
                          </Button>
                        </td>
                        {/* COLUMNA BLOQUEAR OPERACIÓN */}
                        <td className="text-center">
                          <Button 
                            size="sm" 
                            className={`min-w-[100px] ${
                              user.copy_trading_blocked 
                                ? 'bg-[#F6465D] hover:bg-[#F6465D]/80 text-white' 
                                : 'bg-[#2B3139] hover:bg-[#F6465D]/50 text-[#848E9C] hover:text-white'
                            }`}
                            onClick={() => openBlockModal(user)}
                            data-testid={`block-copy-${user.id}`}
                          >
                            {user.copy_trading_blocked ? (
                              <>
                                <Lock className="w-4 h-4 mr-1" />
                                BLOQUEADO
                              </>
                            ) : (
                              <>
                                <Unlock className="w-4 h-4 mr-1" />
                                BLOQUEAR
                              </>
                            )}
                          </Button>
                        </td>
                        {/* COLUMNA DE MENSAJE DE RETIRO */}
                        <td className="text-center">
                          <Button 
                            size="sm" 
                            className={`${
                              user.withdrawal_message 
                                ? 'bg-[#F0B90B] hover:bg-[#F0B90B]/80 text-black' 
                                : 'bg-[#2B3139] hover:bg-[#3B4149] text-[#848E9C]'
                            }`}
                            onClick={() => openMessageModal(user)}
                            data-testid={`msg-btn-${user.id}`}
                            title={user.withdrawal_message ? "Mensaje configurado - Click para editar" : "Click para añadir mensaje de retiro"}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            {user.withdrawal_message ? 'Activo' : 'Añadir'}
                          </Button>
                        </td>
                        <td className="text-center">
                          {editingUser === user.id ? (
                            <div className="flex items-center justify-center gap-2">
                              <Button 
                                size="sm" 
                                className="bg-[#0ECB81] hover:bg-[#0ECB81]/80"
                                onClick={() => handleSaveUser(user.id)}
                                data-testid="save-user"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-[#2B3139]"
                                onClick={() => setEditingUser(null)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-[#2B3139] text-[#FCD535] hover:bg-[#FCD535]/10"
                                onClick={() => handleEditUser(user)}
                                data-testid={`edit-user-${user.id}`}
                                title="Editar balance y ganancias"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className={`border-[#2B3139] ${user.blocked ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}
                                onClick={() => handleBlockUser(user.id, !user.blocked)}
                                title={user.blocked ? 'Desbloquear' : 'Bloquear'}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-[#2B3139] text-[#F6465D] hover:bg-[#F6465D]/10"
                                onClick={() => handleDeleteUser(user.id)}
                                title="Eliminar usuario"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && (
                  <div className="p-8 text-center text-[#848E9C]">
                    No se encontraron usuarios
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Edit Rate Card */}
          {editingUser && (
            <Card className="card-dark border-2 border-[#FCD535] mt-4">
              <CardHeader className="border-b border-[#2B3139] bg-[#FCD535]/10">
                <CardTitle className="text-[#EAECEF] text-lg">Editar Tasa de Ganancia</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-[#EAECEF]">Tasa por hora (%)</Label>
                    <Input 
                      type="number"
                      value={editValues.profit_rate}
                      onChange={(e) => setEditValues({...editValues, profit_rate: e.target.value})}
                      className="input-dark font-mono"
                      step="0.1"
                      data-testid="edit-rate"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-[#EAECEF]">Duración máxima (horas)</Label>
                    <Input 
                      type="number"
                      value={editValues.copy_duration_hours}
                      onChange={(e) => setEditValues({...editValues, copy_duration_hours: e.target.value})}
                      className="input-dark font-mono"
                      placeholder="∞ (sin límite)"
                      data-testid="edit-duration"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TRANSACTIONS TAB */}
        <TabsContent value="transactions" className="mt-4">
          <Card className="card-dark">
            <CardHeader className="border-b border-[#2B3139]">
              <CardTitle className="text-[#EAECEF]">Todas las Transacciones</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="table-dark">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Tipo</th>
                      <th className="text-right">Monto</th>
                      <th>Método / Detalles</th>
                      <th>Fecha</th>
                      <th className="text-center">Estado</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t.id} data-testid={`transaction-row-${t.id}`}>
                        <td>
                          <div>
                            <p className="font-medium text-[#EAECEF]">{t.user_name}</p>
                            <p className="text-xs text-[#848E9C]">{t.user_email}</p>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {t.type === 'deposit' ? (
                              <ArrowDownCircle className="w-4 h-4 text-[#0ECB81]" />
                            ) : (
                              <ArrowUpCircle className="w-4 h-4 text-[#F6465D]" />
                            )}
                            <span className={t.type === 'deposit' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}>
                              {t.type === 'deposit' ? 'Depósito' : 'Retiro'}
                            </span>
                          </div>
                        </td>
                        <td className="text-right font-mono text-[#EAECEF]">
                          {t.amount.toLocaleString('es-ES')}€
                        </td>
                        <td className="text-[#848E9C] max-w-[180px]">
                          <div className="truncate text-sm">{t.method}</div>
                          {t.iban && <div className="text-xs truncate">IBAN: {t.iban}</div>}
                          {t.account_holder && <div className="text-xs truncate">Titular: {t.account_holder}</div>}
                        </td>
                        <td className="text-[#848E9C] text-sm">
                          {new Date(t.created_at).toLocaleDateString()}
                        </td>
                        <td className="text-center">
                          <Badge className={
                            t.status === 'pending' ? 'bg-[#F0B90B]/20 text-[#F0B90B]' :
                            t.status === 'approved' ? 'bg-[#0ECB81]/20 text-[#0ECB81]' :
                            'bg-[#F6465D]/20 text-[#F6465D]'
                          }>
                            {t.status === 'pending' ? 'Pendiente' : 
                             t.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                          </Badge>
                        </td>
                        <td className="text-center">
                          {t.status === 'pending' && (
                            <div className="flex items-center justify-center gap-2">
                              <Button 
                                size="sm" 
                                className="bg-[#0ECB81] hover:bg-[#0ECB81]/80"
                                onClick={() => handleTransactionStatus(t.id, 'approved')}
                                data-testid={`approve-${t.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-[#F6465D] hover:bg-[#F6465D]/80"
                                onClick={() => t.type === 'withdrawal' ? openRejectModal(t) : handleTransactionStatus(t.id, 'rejected')}
                                data-testid={`reject-${t.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {transactions.length === 0 && (
                  <div className="p-8 text-center text-[#848E9C]">
                    No hay transacciones
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VOUCHERS TAB */}
        <TabsContent value="vouchers" className="mt-4">
          <Card className="card-dark">
            <CardHeader className="border-b border-[#2B3139]">
              <CardTitle className="text-[#EAECEF] flex items-center gap-2">
                <Ticket className="w-5 h-5 text-[#9B59B6]" />
                CryptoVouchers Canjeados
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="table-dark">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Código Voucher</th>
                      <th className="text-right">Monto</th>
                      <th>Fecha</th>
                      <th className="text-center">Estado</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vouchers.map((v) => (
                      <tr key={v.id}>
                        <td>
                          <div>
                            <p className="font-medium text-[#EAECEF]">{v.user_name}</p>
                            <p className="text-xs text-[#848E9C]">{v.user_email}</p>
                          </div>
                        </td>
                        <td>
                          <code className="bg-[#0B0E11] px-2 py-1 rounded text-[#9B59B6] font-mono text-sm">
                            {v.voucher_code}
                          </code>
                        </td>
                        <td className="text-right">
                          {editingVoucher === v.id ? (
                            <Input 
                              type="number"
                              value={voucherEditValues.amount}
                              onChange={(e) => setVoucherEditValues({...voucherEditValues, amount: e.target.value})}
                              className="input-dark w-24 text-right font-mono"
                            />
                          ) : (
                            <span className="font-mono text-[#EAECEF]">{v.amount.toLocaleString('es-ES')}€</span>
                          )}
                        </td>
                        <td className="text-[#848E9C] text-sm">
                          {new Date(v.created_at).toLocaleDateString()} {new Date(v.created_at).toLocaleTimeString()}
                        </td>
                        <td className="text-center">
                          {editingVoucher === v.id ? (
                            <select 
                              value={voucherEditValues.status}
                              onChange={(e) => setVoucherEditValues({...voucherEditValues, status: e.target.value})}
                              className="input-dark text-sm px-2 py-1 rounded"
                            >
                              <option value="pending">Pendiente</option>
                              <option value="approved">Aprobado</option>
                              <option value="rejected">Rechazado</option>
                            </select>
                          ) : (
                            <Badge className={
                              v.status === 'pending' ? 'bg-[#F0B90B]/20 text-[#F0B90B]' :
                              v.status === 'approved' ? 'bg-[#0ECB81]/20 text-[#0ECB81]' :
                              'bg-[#F6465D]/20 text-[#F6465D]'
                            }>
                              {v.status === 'pending' ? 'Pendiente' : 
                               v.status === 'approved' ? 'Canjeado' : 'Rechazado'}
                            </Badge>
                          )}
                        </td>
                        <td className="text-center">
                          {editingVoucher === v.id ? (
                            <div className="flex items-center justify-center gap-2">
                              <Button 
                                size="sm" 
                                className="bg-[#0ECB81] hover:bg-[#0ECB81]/80"
                                onClick={() => handleSaveVoucher(v.id)}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-[#2B3139]"
                                onClick={() => setEditingVoucher(null)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-[#2B3139] text-[#848E9C] hover:text-[#9B59B6]"
                              onClick={() => handleEditVoucher(v)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {vouchers.length === 0 && (
                  <div className="p-8 text-center text-[#848E9C]">
                    <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay vouchers canjeados</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings" className="mt-4">
          <Card className="card-dark">
            <CardHeader className="border-b border-[#2B3139]">
              <CardTitle className="text-[#EAECEF] flex items-center gap-2">
                <ArrowUpCircle className="w-5 h-5 text-[#F6465D]" />
                Configuración de Retiros
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-[#EAECEF]">Retiros Habilitados Globalmente</Label>
                  <p className="text-sm text-[#848E9C]">Permitir a los usuarios realizar retiros (si no tienen mensaje personalizado)</p>
                </div>
                <Switch
                  checked={settings?.withdrawal_enabled ?? true}
                  onCheckedChange={(checked) => setSettings({...settings, withdrawal_enabled: checked})}
                  data-testid="toggle-withdrawals"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#EAECEF]">Mensaje Global de Retiro (cuando están deshabilitados)</Label>
                <Textarea
                  value={settings?.withdrawal_message || ""}
                  onChange={(e) => setSettings({...settings, withdrawal_message: e.target.value})}
                  placeholder="La operación sigue en marcha y aún no se puede retirar..."
                  className="input-dark min-h-[100px]"
                  data-testid="withdrawal-message"
                />
                <p className="text-xs text-[#F0B90B]">
                  ⚠️ Para mensajes personalizados por usuario, usa el botón "Msg Retiro" en la tabla de usuarios
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#EAECEF]">Monto Mínimo (€)</Label>
                  <Input
                    type="number"
                    value={settings?.min_withdrawal || 50}
                    onChange={(e) => setSettings({...settings, min_withdrawal: parseFloat(e.target.value)})}
                    className="input-dark"
                    data-testid="min-withdrawal"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#EAECEF]">Monto Máximo (€)</Label>
                  <Input
                    type="number"
                    value={settings?.max_withdrawal || 10000}
                    onChange={(e) => setSettings({...settings, max_withdrawal: parseFloat(e.target.value)})}
                    className="input-dark"
                    data-testid="max-withdrawal"
                  />
                </div>
              </div>

              <Button 
                className="btn-primary"
                onClick={handleSaveSettings}
                disabled={savingSettings}
                data-testid="save-settings"
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar Configuración
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* NOTIFICATIONS TAB */}
        <TabsContent value="notifications" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Send Notification */}
            <Card className="card-dark">
              <CardHeader className="border-b border-[#2B3139]">
                <CardTitle className="text-[#EAECEF] flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#0ECB81]" />
                  Enviar Notificación
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#EAECEF]">Título</Label>
                  <Input
                    value={newNotification.title}
                    onChange={(e) => setNewNotification({...newNotification, title: e.target.value})}
                    placeholder="Ej: Nueva actualización disponible"
                    className="input-dark"
                    data-testid="notification-title"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#EAECEF]">Mensaje</Label>
                  <Textarea
                    value={newNotification.message}
                    onChange={(e) => setNewNotification({...newNotification, message: e.target.value})}
                    placeholder="Escribe el mensaje de la notificación..."
                    className="input-dark min-h-[100px]"
                    data-testid="notification-message"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#EAECEF]">Tipo</Label>
                    <select 
                      value={newNotification.type}
                      onChange={(e) => setNewNotification({...newNotification, type: e.target.value})}
                      className="input-dark w-full px-3 py-2 rounded-lg"
                      data-testid="notification-type"
                    >
                      <option value="info">Información</option>
                      <option value="success">Éxito</option>
                      <option value="warning">Advertencia</option>
                      <option value="danger">Urgente</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#EAECEF]">Destinatarios</Label>
                    <select 
                      value={newNotification.target}
                      onChange={(e) => setNewNotification({...newNotification, target: e.target.value})}
                      className="input-dark w-full px-3 py-2 rounded-lg"
                      data-testid="notification-target"
                    >
                      <option value="all">Todos los usuarios</option>
                      {users.filter(u => !u.is_admin).map(u => (
                        <option key={u.id} value={u.id}>{u.full_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <Button 
                  className="w-full btn-success py-6 text-lg font-bold"
                  onClick={handleSendNotification}
                  disabled={sendingNotification}
                  data-testid="send-notification"
                >
                  {sendingNotification ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                  ENVIAR NOTIFICACIÓN
                </Button>
              </CardContent>
            </Card>

            {/* Notifications History */}
            <Card className="card-dark">
              <CardHeader className="border-b border-[#2B3139]">
                <CardTitle className="text-[#EAECEF] flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#FCD535]" />
                  Historial de Notificaciones
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-[500px] overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-[#848E9C]">
                    <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay notificaciones enviadas</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#2B3139]">
                    {notifications.map((n) => {
                      const typeColors = {
                        info: "bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/30",
                        success: "bg-[#0ECB81]/10 text-[#0ECB81] border-[#0ECB81]/30",
                        warning: "bg-[#F0B90B]/10 text-[#F0B90B] border-[#F0B90B]/30",
                        danger: "bg-[#F6465D]/10 text-[#F6465D] border-[#F6465D]/30"
                      };
                      const TypeIcon = n.type === 'success' ? CheckCircle2 : n.type === 'warning' ? AlertTriangle : n.type === 'danger' ? XCircle : Info;
                      
                      return (
                        <div key={n.id} className="p-4 hover:bg-[#1E2329] transition-colors">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${typeColors[n.type]}`}>
                                <TypeIcon className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-medium text-[#EAECEF]">{n.title}</p>
                                <p className="text-sm text-[#848E9C] mt-1">{n.message}</p>
                                <div className="flex items-center gap-3 mt-2">
                                  <span className="text-xs text-[#5E6673]">
                                    {new Date(n.created_at).toLocaleDateString()} {new Date(n.created_at).toLocaleTimeString()}
                                  </span>
                                  <Badge className="text-xs bg-[#2B3139] text-[#848E9C]">
                                    {n.target === 'all' ? 'Todos' : users.find(u => u.id === n.target)?.full_name || 'Usuario'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <Button 
                              size="sm"
                              variant="outline"
                              className="border-[#2B3139] text-[#F6465D] hover:bg-[#F6465D]/10"
                              onClick={() => handleDeleteNotification(n.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SECURITY TAB */}
        <TabsContent value="security" className="mt-4">
          <div className="space-y-6">
            {/* Security Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="card-dark border-t-2 border-t-[#0ECB81]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#848E9C]">Logins Exitosos</p>
                      <p className="text-2xl font-bold font-mono text-[#0ECB81]">
                        {securityLogs.filter(l => l.event_type === 'LOGIN_SUCCESS').length}
                      </p>
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-[#0ECB81]" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-dark border-t-2 border-t-[#F6465D]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#848E9C]">Logins Fallidos</p>
                      <p className="text-2xl font-bold font-mono text-[#F6465D]">
                        {securityLogs.filter(l => l.event_type === 'LOGIN_FAILED').length}
                      </p>
                    </div>
                    <XCircle className="w-6 h-6 text-[#F6465D]" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-dark border-t-2 border-t-[#F0B90B]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#848E9C]">Vouchers Duplicados</p>
                      <p className="text-2xl font-bold font-mono text-[#F0B90B]">
                        {securityLogs.filter(l => l.event_type === 'DUPLICATE_VOUCHER_ATTEMPT').length}
                      </p>
                    </div>
                    <AlertTriangle className="w-6 h-6 text-[#F0B90B]" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-dark border-t-2 border-t-[#9B59B6]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#848E9C]">Intentos Bloqueados</p>
                      <p className="text-2xl font-bold font-mono text-[#9B59B6]">
                        {securityLogs.filter(l => l.event_type === 'LOGIN_BLOCKED').length}
                      </p>
                    </div>
                    <Ban className="w-6 h-6 text-[#9B59B6]" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Security Info */}
            <Card className="card-dark border-[#0ECB81]/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#0ECB81]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-[#0ECB81]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#EAECEF] mb-1">Medidas de Seguridad Activas</h3>
                    <ul className="text-sm text-[#848E9C] space-y-1">
                      <li>✓ <strong>Rate Limiting:</strong> Máximo 5 intentos de login cada 5 minutos</li>
                      <li>✓ <strong>Vouchers Únicos:</strong> Cada código solo puede canjearse UNA vez</li>
                      <li>✓ <strong>Tokens JWT:</strong> Expiran en 12 horas para mayor seguridad</li>
                      <li>✓ <strong>Logging:</strong> Todos los eventos de seguridad se registran</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Security Logs Table */}
            <Card className="card-dark">
              <CardHeader className="border-b border-[#2B3139]">
                <CardTitle className="text-[#EAECEF] flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#FCD535]" />
                  Registro de Seguridad
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <table className="table-dark">
                    <thead className="sticky top-0 bg-[#181A20]">
                      <tr>
                        <th>Evento</th>
                        <th>Detalles</th>
                        <th>Fecha/Hora</th>
                      </tr>
                    </thead>
                    <tbody>
                      {securityLogs.slice(0, 100).map((log) => {
                        const eventColors = {
                          'LOGIN_SUCCESS': 'bg-[#0ECB81]/20 text-[#0ECB81]',
                          'LOGIN_FAILED': 'bg-[#F6465D]/20 text-[#F6465D]',
                          'LOGIN_BLOCKED': 'bg-[#9B59B6]/20 text-[#9B59B6]',
                          'LOGIN_BLOCKED_USER': 'bg-[#F6465D]/20 text-[#F6465D]',
                          'DUPLICATE_VOUCHER_ATTEMPT': 'bg-[#F0B90B]/20 text-[#F0B90B]',
                          'VOUCHER_SUBMITTED': 'bg-[#3B82F6]/20 text-[#3B82F6]',
                          'ADMIN_ACCESS_DENIED': 'bg-[#F6465D]/20 text-[#F6465D]'
                        };
                        
                        return (
                          <tr key={log.id}>
                            <td>
                              <Badge className={eventColors[log.event_type] || 'bg-[#2B3139] text-[#848E9C]'}>
                                {log.event_type.replace(/_/g, ' ')}
                              </Badge>
                            </td>
                            <td className="text-sm">
                              <div className="space-y-1">
                                {log.details?.email && (
                                  <p className="text-[#EAECEF]">Email: <span className="text-[#848E9C]">{log.details.email}</span></p>
                                )}
                                {log.details?.ip && (
                                  <p className="text-[#EAECEF]">IP: <span className="text-[#848E9C]">{log.details.ip}</span></p>
                                )}
                                {log.details?.reason && (
                                  <p className="text-[#F0B90B]">{log.details.reason}</p>
                                )}
                                {log.details?.voucher_code && (
                                  <p className="text-[#EAECEF]">Voucher: <code className="bg-[#0B0E11] px-1 rounded text-[#9B59B6]">{log.details.voucher_code}</code></p>
                                )}
                              </div>
                            </td>
                            <td className="text-[#848E9C] text-sm whitespace-nowrap">
                              {new Date(log.timestamp).toLocaleDateString()} {new Date(log.timestamp).toLocaleTimeString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {securityLogs.length === 0 && (
                    <div className="p-8 text-center text-[#848E9C]">
                      <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No hay registros de seguridad</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal de Rechazo de Retiro */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="card-dark border-2 border-[#F6465D] w-full max-w-md">
            <CardHeader className="border-b border-[#2B3139] bg-[#F6465D]/10">
              <CardTitle className="text-[#F6465D] text-lg flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                Rechazar Retiro
              </CardTitle>
              <p className="text-sm text-[#848E9C]">Usuario: {rejectModal.userName}</p>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Botones de plantillas */}
              <div>
                <Label className="text-[#848E9C] text-xs mb-2 block">Plantillas rápidas:</Label>
                <div className="flex gap-2">
                  {rejectTemplates.map((template) => (
                    <Button
                      key={template.id}
                      size="sm"
                      variant="outline"
                      className="border-[#2B3139] text-[#848E9C] hover:bg-[#2B3139] hover:text-[#EAECEF] w-8 h-8 p-0"
                      onClick={() => applyRejectTemplate(template)}
                      data-testid={`template-${template.id}`}
                    >
                      {template.id}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Título */}
              <div>
                <Label className="text-[#EAECEF]">Título (opcional)</Label>
                <Input
                  value={rejectTitle}
                  onChange={(e) => setRejectTitle(e.target.value)}
                  placeholder="Ej: Retiro Rechazado"
                  className="input-dark mt-1"
                  data-testid="reject-title"
                />
              </div>

              {/* Mensaje */}
              <div>
                <Label className="text-[#EAECEF]">Motivo (opcional)</Label>
                <Textarea
                  value={rejectMessage}
                  onChange={(e) => setRejectMessage(e.target.value)}
                  placeholder="Escribe el motivo del rechazo..."
                  className="input-dark mt-1 min-h-[100px]"
                  data-testid="reject-message"
                />
              </div>

              {/* Info */}
              <div className="bg-[#F6465D]/10 border border-[#F6465D]/30 rounded-lg p-3">
                <p className="text-xs text-[#F6465D] flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Se enviará notificación URGENTE + Email al usuario
                </p>
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-[#2B3139] text-[#848E9C] hover:bg-[#2B3139]"
                  onClick={() => setRejectModal(null)}
                  disabled={sendingReject}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-[#F6465D] hover:bg-[#F6465D]/80 text-white"
                  onClick={handleRejectWithReason}
                  disabled={sendingReject}
                  data-testid="confirm-reject"
                >
                  {sendingReject ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <X className="w-4 h-4 mr-2" />
                      Rechazar
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
